import { useCustomizeMode } from '@/features/experiences/components/CustomizeMode';
import { SCHEDULE_SEND_TO_CALENDAR_TAB } from '@/features/experiences/components/CustomizeMode/customizeOptions';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { getExperienceAddToCalendarAccess } from '@/features/interactions/addToCalendar/access.lib';
import { AddToCalendarConfigAccess, Role } from '@/types/gql.generated';
import { Box, Button, Flex, Text } from '@/ui';
import { useShareModalStore } from './store';

// Who the audience actually is, said plainly. Only the two audiences narrower
// than the link appear: PUBLIC is not a mismatch, and OFF withholds the
// interaction from everyone including the owner — which is a deliberate "no
// calendar tools here" rather than something the share modal should query.
const AUDIENCE_COPY: Partial<Record<AddToCalendarConfigAccess, string>> = {
  [AddToCalendarConfigAccess.Owner]: 'Send to calendar is limited to just you.',
  [AddToCalendarConfigAccess.Collaborator]:
    'Send to calendar is limited to collaborators.',
};

/**
 * Reconciles the two decisions this modal can leave disagreeing: the page is
 * public, but Add to Calendar reaches a narrower audience. Nothing is wrong
 * with that combination — it just isn't what "anyone with the link" implies, so
 * it's said out loud with a way to change it.
 *
 * Owner-only, because the setting it links to is owner-only (the server refuses
 * it from anyone else) and because the mismatch is only actionable by whoever
 * can act on it.
 */
export const AddToCalendarAccessNote = () => {
  const { experience } = usePageExperienceContext();
  const { close } = useShareModalStore();
  const { setActiveControlTab } = useCustomizeMode();

  const access = getExperienceAddToCalendarAccess(experience);
  const audienceCopy = access ? AUDIENCE_COPY[access] : undefined;
  const isOwner = experience?.role === Role.Owner;

  if (!isOwner || !experience?.isPublic || !audienceCopy) {
    return null;
  }

  const handleConfigure = () => {
    close();
    setActiveControlTab('schedule', SCHEDULE_SEND_TO_CALENDAR_TAB);
  };

  return (
    <Box
      bg="yellow.50"
      border="1px solid"
      borderColor="yellow.300"
      borderRadius="ah-xl"
      p="4"
    >
      {/* Baseline-aligned so Manage sits on the heading's line rather than
          floating against the taller text block. The heading takes the wrap;
          the button keeps its width. */}
      <Flex align="baseline" gap="4" justify="space-between">
        <Text fontSize="ah-control" fontWeight={600}>
          Not everyone who can see this schedule can add it to their calendar.
        </Text>
        <Button
          flexShrink={0}
          fontSize="ah-status"
          h="auto"
          size="ah-sm"
          variant="ahGhost"
          onClick={handleConfigure}
        >
          Manage
        </Button>
      </Flex>
      <Text color="ink.muted" fontSize="ah-status" lineHeight={1.45} mt="1">
        {audienceCopy}
      </Text>
    </Box>
  );
};
